import { debounce, prefersReducedMotion } from '@theme/utilities';

const MOBILE_MQ = '(max-width: 989px)';

/**
 * Below 990px viewport width, runs USPs in a continuous horizontal marquee (duplicated strip, CSS animation).
 */
class TopNavUspsAutoscroll extends HTMLElement {
  /** @type {HTMLDivElement | null} */
  #marqueeEl = null;
  /** @type {HTMLDivElement | null} */
  #segment = null;
  /** @type {MediaQueryList | undefined} */
  #mq;

  connectedCallback() {
    this.#mq = window.matchMedia(MOBILE_MQ);
    this.#mq.addEventListener('change', this.#onMqChange);

    this.addEventListener('pointerenter', this.#pause);
    this.addEventListener('pointerleave', this.#resume);
    document.addEventListener('visibilitychange', this.#onVisibility);

    requestAnimationFrame(() => this.#syncLayout());
  }

  disconnectedCallback() {
    this.#debouncedUpdateDuration.cancel();
    this.#teardownMarquee();
    this.#mq?.removeEventListener('change', this.#onMqChange);
    this.removeEventListener('pointerenter', this.#pause);
    this.removeEventListener('pointerleave', this.#resume);
    document.removeEventListener('visibilitychange', this.#onVisibility);
    window.removeEventListener('resize', this.#debouncedUpdateDuration);
  }

  get #speed() {
    const raw = this.dataset.marqueeSpeed;
    const n = parseInt(String(raw), 10);
    if (Number.isNaN(n)) return 5;
    return Math.min(10, Math.max(1, n));
  }

  #onMqChange = () => this.#syncLayout();

  #onVisibility = () => {
    if (document.hidden) this.#pause();
    else this.#resume();
  };

  #pause = () => {
    this.classList.add('top-nav__usps--marquee-paused');
  };

  #resume = () => {
    if (this.matches(':hover') || document.hidden) return;
    this.classList.remove('top-nav__usps--marquee-paused');
  };

  #debouncedUpdateDuration = debounce(() => this.#updateDuration(), 150);

  /** USPs are either direct children or inside the first marquee segment */
  #uspCount() {
    const firstSegment = this.querySelector(':scope > .top-nav__usps-marquee > .top-nav__usps-marquee-segment');
    if (firstSegment) {
      return firstSegment.querySelectorAll(':scope > .top-nav-usp').length;
    }
    return this.querySelectorAll(':scope > .top-nav-usp').length;
  }

  #syncLayout() {
    const count = this.#uspCount();
    const shouldMarquee =
      Boolean(this.#mq?.matches) && !prefersReducedMotion() && count >= 2;

    if (!shouldMarquee) {
      this.#teardownMarquee();
      return;
    }

    if (this.classList.contains('top-nav__usps--marquee') && this.#segment) {
      this.#updateDuration();
      return;
    }

    const flatItems = Array.from(this.querySelectorAll(':scope > .top-nav-usp'));
    const marquee = document.createElement('div');
    marquee.className = 'top-nav__usps-marquee';
    const segment = document.createElement('div');
    segment.className = 'top-nav__usps-marquee-segment';

    flatItems.forEach((el) => segment.appendChild(el));
    marquee.appendChild(segment);

    const clone = /** @type {HTMLElement} */ (segment.cloneNode(true));
    clone.setAttribute('aria-hidden', 'true');
    clone.querySelectorAll('[id]').forEach((el) => el.removeAttribute('id'));
    clone.querySelectorAll('a[href], button, [tabindex]:not([tabindex="-1"])').forEach((el) => {
      el.setAttribute('tabindex', '-1');
    });
    marquee.appendChild(clone);

    this.appendChild(marquee);
    this.#marqueeEl = marquee;
    this.#segment = segment;
    this.classList.add('top-nav__usps--marquee');

    window.addEventListener('resize', this.#debouncedUpdateDuration);
    this.#updateDuration();
  }

  #teardownMarquee() {
    window.removeEventListener('resize', this.#debouncedUpdateDuration);
    this.classList.remove('top-nav__usps--marquee', 'top-nav__usps--marquee-paused');
    this.style.removeProperty('--top-nav-marquee-duration');

    if (!this.#segment || !this.#marqueeEl) {
      this.#marqueeEl = null;
      this.#segment = null;
      return;
    }

    const items = Array.from(this.#segment.querySelectorAll('.top-nav-usp'));
    for (const el of items) {
      this.insertBefore(el, this.#marqueeEl);
    }
    this.#marqueeEl.remove();
    this.#marqueeEl = null;
    this.#segment = null;
  }

  #updateDuration() {
    if (!this.#segment) return;

    const segmentWidth = this.#segment.getBoundingClientRect().width;
    const styles = getComputedStyle(this);
    const gapRaw = styles.getPropertyValue('--top-nav-items-gap').trim();
    const gap = parseFloat(gapRaw) || 20;

    /** Higher speed setting → more px/s → shorter duration */
    const pxPerSec = 16 + this.#speed * 10;
    const travelPx = segmentWidth + gap;
    const durationSec = Math.max(6, travelPx / pxPerSec);

    this.style.setProperty('--top-nav-marquee-duration', `${durationSec}s`);
  }
}

if (!customElements.get('top-nav-usps-autoscroll')) {
  customElements.define('top-nav-usps-autoscroll', TopNavUspsAutoscroll);
}
